/* ============================================================
   mt-ab-variants.js — variant CONFIG (data only).
   Loaded synchronously in <head> BEFORE mt-ab.js.

   ## Source of truth
   `Tarot Content/Ads/Millennial/Testing/Cross-Sprint/Phase A LP Copy Review.xlsx`
   Tabs: OCCULT-Belief, META-Therapy, MILLENNIAL-Era, SASSY-Polite.
   The "SASSY-Polite" tab is emitted as variant key `sassy_attitude` (renamed
   2026-05-12 — Attitude won engagement; Polite's lone sale was Sprint 4 noise).

   ## Preview a variant
   https://millennialtarot.com/?ab_theme_v1=occult_belief
   https://millennialtarot.com/?ab_theme_v1=meta_therapy
   https://millennialtarot.com/?ab_theme_v1=millennial_era
   https://millennialtarot.com/?ab_theme_v1=sassy_attitude
   https://millennialtarot.com/?ab_theme_v1=default
   ============================================================ */
window.MT_AB_VARIANTS = {


  // Demo video vs the original pinned-polaroids "In the Wild" section. Both
  // slots render into sec_inthewild; CSS keyed on the html.mt-ab-demo_video_v1-*
  // class decides which one shows (mt-home.css, "Demo-video slot").
  //
  // LIVE 50/50 AS OF 2026-09-03. This is the re-run the ad-plan review asked
  // for: the first attempt ran hours on 2026-08-24 and was aborted when video
  // shipped to 100%, alongside five other same-day site changes — which is
  // exactly why the August read was uninterpretable. LEAVE THIS ALONE FOR TWO
  // WEEKS. Do not ship other home-page changes while it runs; that is the
  // mistake being corrected. Preview a specific side:
  //   https://millennialtarot.com/?ab_demo_video_v1=video
  //   https://millennialtarot.com/?ab_demo_video_v1=polaroids
  //
  // Fails safe: if this JS never runs, the CSS default shows the video.
  // ----- Hero scene tone (2026-09-29) -----
  // The scene-photo hero (snippets/mt-hero-scene.liquid) in two tones. LIVE on
  // the home page since 2026-09-29, held at 100% dark: the light photos still
  // have the old box faces (Scott is editing them by hand). To run the test,
  // swap in the edited light photos, then set 50/50.
  // Preview a side: ?ab_hero_scene_v1=dark · ?ab_hero_scene_v1=light
  // Fails safe: with no class, the CSS default is dark.
  hero_scene_v1: {
    description: 'Scene hero: night glam (dark) vs sunlit (light). URL: ?ab_hero_scene_v1=<variant>',
    weights: {
      dark: 100,
      light: 0
    },
    variants: {
      dark: {},
      light: {}
    }
  },

  demo_video_v1: {
    description: 'Deck demo video vs In-the-Wild polaroids. URL: ?ab_demo_video_v1=<variant>',
    // `primary` = the experiment reported to GA4 as the user-scoped
    // mt_experiment_id / mt_variant_id pair (see mt-ab.js). Moved here from
    // theme_v1 on 2026-09-03 per that block's own instruction: theme_v1 varies
    // by ad URL rather than at random, so it is a reporting dimension
    // confounded with audience. This is the first genuinely randomized split.
    // 2026-09-30: `primary` moved to offer_v1 (set at the foot of this file) for
    // the Stg 7 headline split; this test keeps its cart attributes and Clarity tags.
    // 2026-10-05: the redesigned home page shows neither arm, so the split stopped meaning anything. Everyone gets
    // `video` now (the test is effectively retired); reads of this experiment end at the home launch.
    weights: {
      video: 100,
      polaroids: 0
    },
    variants: {
      video: {},
      polaroids: {}
    }
  },

  theme_v1: {
    description: 'Phase A: theme axis on single-deck. URL: ?ab_theme_v1=<variant>',
    // `primary` moved to demo_video_v1 on 2026-09-03 — see the note there.
    // theme_v1 varies by AD URL, not at random, so it was always a REPORTING
    // dimension rather than a test: theme is confounded with audience. It
    // keeps working as an ad-targeted copy switch; it just no longer claims
    // the GA4 experiment slot.
    weights: {
      default: 100,
      occult_belief: 0,
      meta_therapy: 0,
      millennial_era: 0,
      sassy_attitude: 0
    },
    variants: {
      default: {},

      // ----- OCCULT-Belief — "the woo isn't you" · 6.12% CTR · 36% eng60 -----
      occult_belief: {
        text: {
          'hero.eyebrow': '✦ WOO-LIGHT. SKEPTIC-FRIENDLY.',
          'hero.h1': 'Tarot with no belief or buy-in required.',
          'hero.h2': 'Because the woo isn\'t you.',
          'hero.body': 'Cards called Sunday Scaries (9 of Swords), Quarter-Life Crisis (Judgement), and Dumpster Fire (the Tower). Pull one. It instantly makes sense.',
          'hero.featured-quote': '"I get what the cards are trying to say immediately. The traditional deck didn\'t resonate as much, but this one speaks directly to my life experiences."',
          'hero.featured-quote-attr': '— @fairlighttarot, Instagram',
          'why.eyebrow': '✦ Why We Made It.',
          'why.h1': 'We like Tarot. But it required belief or buy-in. This doesn\'t.',
          'why.sub': 'Same 78 cards. No belief required.',
          'why.before-content': 'You pull a card? "Not really my thing..."',
          'why.before-body': 'It\'s a guy in a robe holding a stick. You\'re supposed to buy into the universe to make him meaningful. You don\'t, and shouldn\'t have to.',
          'why.before-bullet-1': '❌ Get told to "trust the universe"',
          'why.before-bullet-2': '❌ Try to commit to a framework you didn\'t sign up for',
          'why.before-bullet-3': '❌ Put the deck away with the rest of the woo',
          'why.after-content': 'You pull a card? "Damn, I know what that\'s about."',
          'why.after-body': 'It\'s Big D Energy. You don\'t need a guidebook to tell you what that means. You\'ve already lived this card.',
          'why.after-bullet-1': '✦ Pull a card. Read the name.',
          'why.after-bullet-2': '✦ Instant understanding. No decoding required.',
          'why.after-bullet-3': '✦ That\'s a reading. You\'re doing it.',
          // why.featured-quote intentionally omitted — falls through to default
          // (@fairlighttarot) instead of the Reddit/r/SecularTarot quote that
          // wasn't actually about MT.
          'demo.eyebrow': '✦ NOT SURE YOU BUY IT?',
          'demo.h1': 'Pull a card. Reflection, not prediction.',
          'demo.featured-quote': '"A beautiful gateway deck for someone who needed an excuse to buy tarot cards."',
          'demo.featured-quote-attr': '— Stine, Verified Purchase',
          'guidebook.eyebrow': '✦ 132+ Pages of Real Tarot.',
          'guidebook.h1': 'Real tarot. Woo-light and no buy-in.',
          'guidebook.sub': '132+ pages, written by someone who\'s been to therapy. Every card is a real-life situation — translated into plain, honest, slightly funny English. No robes. No riddles. No "consult your inner priestess."',
          'guidebook.structure-1': '"TL;DR" The whole reading in one line if that\'s all you need.',
          'guidebook.structure-2': '"The Sitch." What the card is actually saying about your week.',
          'guidebook.structure-3': '"Real Talk." The deeper guidance — what to do with it, said plainly.',
          'guidebook.structure-4': '"Reverse, Reverse." When the vibe flips, we tell you exactly how.',
          'guidebook.featured-quote': '"Everything I hoped it would be: entertaining, spot on, and in many cases, incredibly thought-provoking."',
          'guidebook.featured-quote-attr': '— @queen.of.arcana, Instagram',
          'reviews.eyebrow': '✦ From Tarot Creators and Real Owners.',
          'reviews.h1': 'Used nightly by people who don\'t buy the woo.',
          'reviews.featured': '"A beautiful gateway deck for someone who needed an excuse to buy tarot cards."',
          'reviews.featured-attribution': 'Stine, Verified Purchase',
          // founder.eyebrow/h1/body/scott-bio/hailey-bio overrides REMOVED 2026-08-24:
          // the about-section copy now lives only in snippets/mt-about.liquid (Scott's
          // rewrite). The harness was silently masking Liquid edits to that section.
          'founder.featured-quote': '"It captures the feeling of being a millennial in this world perfectly."',
          'founder.featured-quote-attr': '— @midheaventarot, Instagram',
          'inthewild.quote-2': '"It gives me a different lens to think about something — almost like a prompt that helps me step back and see things differently. It\'s grounding in that way."',
          'inthewild.quote-2-attr': '— Caroline, Verified Purchase',
        }
      },

      // ----- META-Therapy — "tarot that's been to therapy" · 9.41% CTR -----
      meta_therapy: {
        text: {
          'hero.eyebrow': '✦ THERAPY-FLUENT. SELF-AWARE.',
          'hero.h1': 'Tarot that\'s been to therapy.',
          'hero.h2': 'Because the wisdom felt outdated.',
          'hero.body': 'Cards called Sunday Scaries (9 of Swords), Quarter-Life Crisis (Judgement), and Dumpster Fire (the Tower). Pull one. It instantly makes sense.',
          'hero.featured-quote': '"Tough love, truth, discernment, being held, wisdom."',
          'hero.featured-quote-attr': '— Jade, Survey',
          'why.eyebrow': '✦ Why We Made It.',
          'why.h1': 'We like Tarot. But it hadn\'t been to therapy. This has.',
          'why.sub': 'Same 78 cards. Therapy-fluent without performing therapy.',
          'why.before-content': 'You pull a card? "Not really my thing..."',
          'why.before-body': 'It\'s a guy in a robe holding a stick. The guidebook reads like it was written before therapy was invented. You can\'t get anything actionable from it.',
          'why.before-bullet-1': '❌ Read about your "shadow self"',
          'why.before-bullet-2': '❌ Try to find actionable insight, get mystical reframes',
          'why.before-bullet-3': '❌ Put the deck away. It doesn\'t speak how you think.',
          'why.after-content': 'You pull a card? "Damn, I know what that\'s about."',
          'why.after-body': 'It\'s Big D Energy. The guidebook talks like a friend who\'s done the processing. Same wisdom, made for what came up this week.',
          'why.after-bullet-1': '✦ Pull a card. Read the name.',
          'why.after-bullet-2': '✦ Instant understanding. No decoding required.',
          'why.after-bullet-3': '✦ That\'s a reading. You\'re doing it.',
          'why.featured-quote': '"Tough love, truth, discernment, being held, wisdom."',
          'why.featured-quote-attr': '— Jade, Survey',
          'demo.eyebrow': '✦ WHAT CAME UP?',
          'demo.h1': 'Pull a card. What your therapist would say.',
          'demo.featured-quote': '"I didn\'t expect it to work so well for deeper readings."',
          'demo.featured-quote-attr': '— @TheNewMoonTarot, 150K YouTube',
          'guidebook.eyebrow': '✦ 132+ Pages of Real Tarot.',
          'guidebook.h1': 'Real tarot. Therapy-fluent and self-aware.',
          'guidebook.sub': '132+ pages, written by someone who\'s been to therapy. Every card is a real-life situation — translated into plain, honest, slightly funny English. No robes. No riddles. No "consult your inner priestess."',
          'guidebook.structure-1': '"TL;DR" The whole reading in one line if that\'s all you need.',
          'guidebook.structure-2': '"The Sitch." What the card is actually saying about your week.',
          'guidebook.structure-3': '"Real Talk." The deeper guidance — what to do with it, said plainly.',
          'guidebook.structure-4': '"Reverse, Reverse." When the vibe flips, we tell you exactly how.',
          'guidebook.featured-quote': '"Everything I hoped it would be: entertaining, spot on, and in many cases, incredibly thought-provoking."',
          'guidebook.featured-quote-attr': '— @queen.of.arcana, Instagram',
          'reviews.eyebrow': '✦ From People Who Take It Seriously.',
          'reviews.h1': 'Used nightly to process the week.',
          'reviews.featured': '"Tough love, truth, discernment, being held, wisdom."',
          'reviews.featured-attribution': 'Jade, Survey',
          'founder.featured-quote': '"It captures the feeling of being a millennial in this world perfectly."',
          'founder.featured-quote-attr': '— @midheaventarot, Instagram',
          'inthewild.quote-2': '"I get what the cards are trying to say immediately. The traditional deck didn\'t resonate as much, but this one speaks directly to my life experiences."',
          'inthewild.quote-2-attr': '— @fairlighttarot, Instagram',
        }
      },

      // ----- MILLENNIAL-Era — "tarot that grew up with you" · 5.44% CTR · 46% eng60 · $55 bundle sale -----
      millennial_era: {
        text: {
          'hero.eyebrow': '✦ TAROT That Gets Y2K AND THE HOUSING CRISIS',
          'hero.h1': 'Tarot that gets being a Millennial.',
          'hero.h2': 'Updated for how we work, live and love.',
          'hero.body': 'Cards called Funemployed (the Hanged Man), Student Loans (Death), and Sunday Scaries (9 of Swords). Pull one. You\'ve already lived this card.',
          'hero.featured-quote': '"It captures the feeling of being a millennial in this world perfectly."',
          'hero.featured-quote-attr': '— @midheaventarot, Instagram',
          'why.eyebrow': '✦ Why We Made It.',
          'why.h1': 'We like Tarot. But it didn\'t get how we live. This does.',
          'why.sub': 'Same 78 cards. For Y2K, AIM, flip phones, and everything since.',
          'why.before-content': 'You pull a card? "Not really my thing..."',
          'why.before-body': 'It\'s a guy in a robe holding a stick. He\'s from a century without student loans, situationships, or quarter-life crisis. You can\'t map his life to yours.',
          'why.before-bullet-1': '❌ Read the guidebook, Google what a "Hierophant" is',
          'why.before-bullet-2': '❌ Try to map "wands" to your actual life',
          'why.before-bullet-3': '❌ Realize this wasn\'t made for your generation',
          'why.after-content': 'You pull a card? "Damn, I know what that\'s about."',
          'why.after-body': 'It\'s Big D Energy. It\'s Sunday Scaries. It\'s Funemployed. Tarot that lived through the same decades you did.',
          'why.after-bullet-1': '✦ Pull a card. Recognize a Sunday Scaries.',
          'why.after-bullet-2': '✦ See yourself in Funemployed.',
          'why.after-bullet-3': '✦ Tarot that grew up with you.',
          'why.featured-quote': '"A love letter to the generation. Very accurate modernization."',
          'why.featured-quote-attr': '— @callingcorners, Instagram',
          'demo.eyebrow': '✦ YOUR LIFE, ON A CARD.',
          'demo.h1': 'Pull a card. See yourself in Sunday Scaries.',
          'demo.featured-quote': '"A love letter to the generation."',
          'demo.featured-quote-attr': '— @callingcorners, Instagram',
          'guidebook.eyebrow': '✦ 132+ Pages of Real Tarot.',
          'guidebook.h1': 'Real tarot. Millennial-fluent.',
          'guidebook.sub': '132+ pages, written by someone who\'s been to therapy. Every card is a real-life situation — translated into plain, honest, slightly funny English. No robes. No riddles. No "consult your inner priestess."',
          'guidebook.structure-1': '"TL;DR" The whole reading in one line if that\'s all you need.',
          'guidebook.structure-2': '"The Sitch." What the card is actually saying about your week.',
          'guidebook.structure-3': '"Real Talk." The deeper guidance — what to do with it, said plainly.',
          'guidebook.structure-4': '"Reverse, Reverse." When the vibe flips, we tell you exactly how.',
          'guidebook.featured-quote': '"Everything I hoped it would be: entertaining, spot on, and in many cases, incredibly thought-provoking."',
          'guidebook.featured-quote-attr': '— @queen.of.arcana, Instagram',
          'reviews.eyebrow': '✦ From Millennial Owners and Tarot Creators.',
          'reviews.h1': 'Used by the generation that lived it.',
          'reviews.featured': '"It captures the feeling of being a millennial in this world perfectly."',
          'reviews.featured-attribution': '@midheaventarot, Instagram',
          'founder.featured-quote': '"Kind of like an inside joke or secret that hits differently because it\'s millennial-themed."',
          'founder.featured-quote-attr': '— DaVonne, Verified Purchase',
          'inthewild.quote-2': '"Millennial humor at its best — \'dumpster fire\' for the Tower is perfect. Sunday scaries for the Nine of Swords? Genius!"',
          'inthewild.quote-2-attr': '— @TheNewMoonTarot, 150K YouTube',
        }
      },

      // ----- SASSY-Attitude — "calls it like it is" · 4.89% CTR · $34 single sale (renamed from sassy_polite 2026-05-12) -----
      sassy_attitude: {
        text: {
          'hero.eyebrow': '✦ SASSY. DIRECT.',
          'hero.h1': 'Tarot that calls it like it is.',
          'hero.h2': 'Because most readings beat around the bush.',
          'hero.body': 'Cards called Sunday Scaries (9 of Swords), Quarter-Life Crisis (Judgement), and Dumpster Fire (the Tower). Pull one. It instantly makes sense.',
          'hero.featured-quote': '"It\'s delightful, and funny, and so fucking accurate I can\'t help but laugh… It\'s brilliant."',
          'hero.featured-quote-attr': '— @Indiedeckreview, 33K IG',
          'why.eyebrow': '✦ Why We Made It.',
          'why.h1': 'We like Tarot. But it can beat around the bush. This doesn\'t.',
          'why.sub': 'Same 78 cards. No hedging.',
          'why.before-content': 'You pull a card? "Not really my thing..."',
          'why.before-body': 'It\'s a guy in a robe holding a stick. The guidebook reads like it\'s afraid to say anything direct. Vague hedges, no actual takes.',
          'why.before-bullet-1': '❌ Read the guidebook, get vague hedges',
          'why.before-bullet-2': '❌ Try to extract an actual take',
          'why.before-bullet-3': '❌ Put the deck away. It didn\'t say anything.',
          'why.after-content': 'You pull a card? "Damn, I know what that\'s about."',
          'why.after-body': 'It\'s Big D Energy. The guidebook calls it like it sees it. Same wisdom, said the way an honest friend would.',
          'why.after-bullet-1': '✦ Pull a card. Read what it actually says.',
          'why.after-bullet-2': '✦ Get called out (kindly).',
          'why.after-bullet-3': '✦ No beating around the bush.',
          'why.featured-quote': '"Hysterical and weirdly accurate."',
          'why.featured-quote-attr': '— Kathy, Verified Purchase',
          'demo.eyebrow': '✦ OK, CALL ME OUT.',
          'demo.h1': 'Pull a card. It\'ll call it like it is.',
          'demo.featured-quote': '"Hysterical and weirdly accurate."',
          'demo.featured-quote-attr': '— Kathy, Verified Purchase',
          'guidebook.eyebrow': '✦ 132+ Pages of Real Tarot.',
          'guidebook.h1': 'Real tarot. Direct and no hedging.',
          'guidebook.sub': '132+ pages, written by someone who\'s been to therapy. Every card is a real-life situation — translated into plain, honest, slightly funny English. No robes. No riddles. No "consult your inner priestess."',
          'guidebook.structure-1': '"TL;DR" The whole reading in one line if that\'s all you need.',
          'guidebook.structure-2': '"The Sitch." What the card is actually saying about your week.',
          'guidebook.structure-3': '"Real Talk." The deeper guidance — what to do with it, said plainly.',
          'guidebook.structure-4': '"Reverse, Reverse." When the vibe flips, we tell you exactly how.',
          'guidebook.featured-quote': '"So fucking accurate I can\'t help but laugh."',
          'guidebook.featured-quote-attr': '— @Indiedeckreview, 33K IG',
          'reviews.eyebrow': '✦ From Owners Who Wanted The Truth.',
          'reviews.h1': 'Direct. Funny. Used nightly.',
          'reviews.featured': '"Hysterical and weirdly accurate."',
          'reviews.featured-attribution': 'Kathy, Verified Purchase',
          'founder.featured-quote': '"It captures the feeling of being a millennial in this world perfectly."',
          'founder.featured-quote-attr': '— @midheaventarot, Instagram',
          'inthewild.quote-2': '"Hysterical and weirdly accurate."',
          'inthewild.quote-2-attr': '— Kathy, Verified Purchase',
        }
      }
    }
  },

  // headline_v1 REMOVED 2026-07-30. It was a stub that never tested anything:
  // weights were { base: 100, alt_a: 0, alt_b: 0 } so it always resolved to
  // `base`, and both alt arms held empty themeText maps, so even a forced
  // ?ab_headline_v1=alt_a swapped zero copy. Every session still paid for it
  // — a cookie, an <html> class, an mt_ab_exposure event, and (once cart
  // attributes shipped) a dead `mt_ab_headline_v1: base` key on every order.
  // Recover from git if the per-theme H1 rotation ever gets built for real.

  offer_v1: {
    description: 'Phase B-R1: bundle offer/positioning axis. theme_v1=occult_belief is the constant.',
    weights: { default: 0, bundle_theme_anchored: 100, bundle_relational: 0, bundle_clarity: 0, bundle_sassy: 0, bundle_resonance: 0, bundle_2x_mt_gift: 0 },
    variants: {
      default: {},

      // ----- Occult Singular — "the woo isn't you" -----
      bundle_theme_anchored: {
        heroOffer: 'bundle',
        text: {
          'hero.eyebrow': '✦ WOO-LIGHT. SKEPTIC-FRIENDLY.',
          'hero.h1': 'Tarot with no belief or buy-in required.',
          'hero.h2': 'Because the woo isn\'t you.',
          'hero.body': 'The Millennial deck calls the Tower ‘Dumpster Fire’ and the Gen Z deck ‘It’s Giving Doomsday.’ Two decks, two languages, same wisdom. Pull a card. It instantly makes sense.',
          'hero.featured-quote': '"Felt seen by the interpretations."',
          'hero.featured-quote-attr': '— DaVonne, customer',

          'why.eyebrow': '✦ Why We Made It.',
          'why.h1': 'We love tarot. The woo just isn\'t us.',
          'why.sub': 'So we made one with no belief or buy-in required. Same 78 cards, two decks.',
          'why.before-content': 'You pull a card? "Not really my thing..."',
          'why.before-body': 'It\'s a guy in a robe holding a stick. You\'re not sure what it means, or what you\'re even signing up for. Feels like a lot. So it goes back in the box.',
          'why.before-bullet-1': '❌ Get told to "trust the universe"',
          'why.before-bullet-2': '❌ Try to commit to a framework you didn\'t sign up for',
          'why.before-bullet-3': '❌ Put the deck away with the rest of the woo',
          'why.after-content': 'You pull a card? "Damn, I know what that\'s about."',
          'why.after-body': 'Same card. One deck calls it \'Big D Energy.\' The other calls it \'The Rizzler.\' No guessing. No buy-in. You already know it.',
          'why.after-bullet-1': '✦ Pull a card. Read the name.',
          'why.after-bullet-2': '✦ Instant understanding. No decoding required.',
          'why.after-bullet-3': '✦ That\'s a reading. You\'re doing it.',

          'demo.eyebrow': '✦ NOT SURE YOU BUY IT?',
          'demo.h1': 'Pull a card. Reflection, not prediction.',
          'demo.featured-quote': '"A beautiful gateway deck for someone who needed an excuse to buy tarot cards."',
          'demo.featured-quote-attr': '— Stine, customer',

          'guidebook.eyebrow': '✦ 132+ Page Guidebook of Real Tarot. (per deck)',
          'guidebook.h1': 'Real Tarot. That makes you feel seen.',
          'guidebook.sub': '132+ pages per deck, written by people who’ve been to therapy. Every card is a real-life situation — translated into plain, honest, slightly funny English. No robes. No riddles. No "consult your inner priestess."',

          'reviews.eyebrow': '✦ From Tarot Creators and Real Owners.',
          'reviews.h1': 'For people who like tarot, but the woo isn\'t them.',
          'reviews.featured': '"A beautiful gateway deck for someone who needed an excuse to buy tarot cards."',
          'reviews.featured-attribution': 'Stine, customer',

          'inthewild.lead': '"Felt seen by the interpretations."',
          'inthewild.lead-attr': '— DaVonne, customer',
          'inthewild.quote-2': '"It\'s delightful, funny, and so freaking accurate I can\'t help but laugh. It\'s brilliant."',
          'inthewild.quote-2-attr': '— @indiedeckreview, 33K Instagram',
          'inthewild.quote-3': '"I thought it was going to be a novelty deck. It\'s definitely more than that."',
          'inthewild.quote-3-attr': '— @thestarmeanshope',

          'founder.featured-quote': '"It captures the feeling of being a millennial in this world perfectly."',
          'founder.featured-quote-attr': '— @midheaventarot, Instagram',
        }
      },

      // ----- Occult Friends — "your friends aren't really tarot people" -----
      bundle_relational: {
        heroOffer: 'bundle',
        text: {
          // H2/body updated 2026-07-31 for Conv R2: social payoff in H2
          // (Scott's line); no-buy-in beat moves to body/why sections.
          // NOTE this hero.eyebrow key is INERT on the bundle hero — it
          // renders hardcoded `hero.eyebrow-bundle` ("✦ Millennial + Gen Z
          // Tarot" with jump links), which already matches Scott's intent.
          'hero.eyebrow': '✦ MILLENNIAL + GEN Z TAROT',
          // Hierarchy flipped 2026-07-30 to match the ad that carries ~89% of
          // paid spend (Conv R1 | peeps-table | 02.01). That ad HOOKS on the
          // relational problem ("You like tarot but your peeps aren't tarot
          // people") and puts no-belief/no-buy-in in the SUPPORT slot ("Tarot
          // with no buy-in" in body, "Two Decks. No belief required." as the
          // headline field). This page led with the support line and buried
          // the hook in H2, so the page was inverted relative to the ad.
          // "peeps" (not "people"/"friends") matches the creative verbatim.
          // H2 reverted to no-buy-in 2026-07-31 (Scott, same day): the table
          // payoff lives in the body close instead, so both beats show once.
          'hero.h1': 'Tarot for when your peeps aren\'t tarot people.',
          'hero.h2': 'No belief or buy-in required.',
          'hero.body': 'The Millennial deck calls the Tower ‘Dumpster Fire’ and the Gen Z deck ‘It’s Giving Doomsday.’ Two decks, two languages, same wisdom. Read for whoever\'s at your table.',
          'hero.featured-quote': '"At girls brunch — opening conversation and looking for new perspectives."',
          'hero.featured-quote-attr': '— Ana, professional reader',

          'why.eyebrow': '✦ Why We Made It.',
          'why.h1': 'We like Tarot. But not everyone in the group gets it. These work for everyone.',
          'why.sub': 'Same 78 cards. Two decks. Anyone can pull one.',
          'why.before-content': 'You pull a card at the table? "Oh… that’s not really my thing."',
          'why.before-body': 'Someone’s a skeptic. Someone’s a believer. Someone’s in between. The deck makes it weird, so it goes back in the box.',
          'why.before-bullet-1': '❌ Pull a card, get a polite "uh-huh" from the table',
          'why.before-bullet-2': '❌ Friend who hates tarot opts out',
          'why.before-bullet-3': '❌ Deck goes back in the box',
          'why.after-content': 'You pull a card? Everyone leans in.',
          'why.after-body': 'It’s Big D Energy. It’s The Rizzler. The names do the work — no belief or buy-in required to laugh at how true it just landed.',
          'why.after-bullet-1': '✦ Pull a card. Read the name out loud.',
          'why.after-bullet-2': '✦ The whole table gets it. Instantly.',
          'why.after-bullet-3': '✦ Anyone can be into it.',
          'why.featured-quote': '"At girls brunch for lunch — opening conversation and looking for new perspectives."',
          'why.featured-quote-attr': '— Ana, Verified Purchase',

          'demo.eyebrow': '✦ TRY IT AT THE TABLE',
          'demo.h1': 'Pull a card. Pass the deck.',
          'demo.featured-quote': '"When friends come over — I want to put it on the center table as a thought starter."',
          'demo.featured-quote-attr': '— Jodi, Verified Purchase',

          'guidebook.eyebrow': '✦ 132+ Page Guidebook of Real Tarot. (per deck)',
          'guidebook.h1': 'Real tarot. Made for everyone at the table.',
          'guidebook.sub': '132+ pages per deck, written by people who’ve been to therapy. Every card is a real-life situation — translated into plain, honest, slightly funny English. No robes. No riddles. No "consult your inner priestess."',

          'reviews.eyebrow': '✦ From People Who Use It Together.',
          'reviews.h1': 'Even the friends who \'aren\'t into tarot\' love it.',
          'reviews.featured': '"When friends come over — I want to put it on the center table as a thought starter."',
          'reviews.featured-attribution': 'Jodi, Verified Purchase',

          'inthewild.lead': '"At girls brunch — opening conversation and looking for new perspectives."',
          'inthewild.lead-attr': '— Ana, professional reader',
          'inthewild.quote-2': '"A great deck to read for friends at parties, for people who aren\'t as into tarot."',
          'inthewild.quote-2-attr': '— Amazon review',
          'inthewild.quote-3': '"At parties with Gen X and Millennials."',
          'inthewild.quote-3-attr': '— Kathy, customer',

          'founder.featured-quote': '"It captures the feeling of being a millennial in this world perfectly."',
          'founder.featured-quote-attr': '— @midheaventarot, Instagram',
        }
      },

      // ----- Clarity — "tarot you instantly know" · Conv R2: swap-this, them-vs-us -----
      // URL-pinned only (weight 0). Hero-block override; deeper sections fall
      // back to the standard narrative. Added 2026-07-31 for per-angle scent match.
      bundle_clarity: {
        heroOffer: 'bundle',
        text: {
          // No eyebrow override: the bundle hero renders the hardcoded
          // `hero.eyebrow-bundle` ("✦ Millennial + Gen Z Tarot" with jump
          // links). Overriding it costs the links (see mt-hero.liquid:72).
          // "The Clearest Tarot Decks" eyebrow idea parked on that trade.
          'hero.h1': 'Tarot you instantly know.',
          'hero.h2': 'Every card translated into Millennial and Gen Z.',
          'hero.body': 'The Millennial deck calls the Tower ‘Dumpster Fire’ and the Gen Z deck ‘It’s Giving Doomsday.’ Two decks, two languages, same wisdom. Pull a card. It instantly makes sense.',
          'hero.featured-quote': '"I get what the cards are trying to say immediately."',
          'hero.featured-quote-attr': '— @fairlighttarot, Instagram'
        }
      },

      // ----- Sassy — "calls it like it is" · Conv R2: fake-post -----
      // URL-pinned only (weight 0). Hero-block override, same fallback rule.
      bundle_sassy: {
        heroOffer: 'bundle',
        text: {
          // No eyebrow override — same hero.eyebrow-bundle rule as bundle_clarity.
          'hero.h1': 'Tarot that calls it like it is.',
          'hero.h2': 'No polite readings that beat around the bush.',
          'hero.body': 'The Millennial deck calls the Tower ‘Dumpster Fire’ and the Gen Z deck ‘It’s Giving Doomsday.’ Two decks, two languages, same wisdom. Pull a card. It instantly makes sense.',
          'hero.featured-quote': '"It\'s delightful, and funny... It\'s brilliant."',
          'hero.featured-quote-attr': '— @Indiedeckreview, Instagram'
        }
      },

      // Round 2 placeholder — leave null.
      bundle_2x_mt_gift: null
    }
  },

  // Hero H1/H2 clarity test (Scott, Jul 3 2026). Registered AFTER offer_v1 on
  // purpose: text deep-merges across experiments and later experiments win on
  // key conflicts, so the alt arm overrides only hero.h1/hero.h2 while every
  // other offer_v1 line stands. Judge on ATC rate via GA4 variant_id
  // (experiment_id = hero_copy_v1); Clarity sessions carry tag
  // mt_ab_hero_copy_v1. QA: ?ab_hero_copy_v1=alt_woo_plain
  // HALTED 2026-07-30 — ran Jul 3-30, produced zero readable result. Two
  // independent instrumentation gaps: (1) GA4's variant_id is an EVENT-scoped
  // custom dimension, so it only ever attaches to mt_ab_exposure and never to
  // add_to_cart / begin_checkout / purchase; (2) mt_ab_exposure itself reached
  // only 193 of 3,902 sessions (5%) because window.gtag arrives after this
  // script and the 10s retry loses bounced mobile ad traffic. Shopify orders
  // carry no variant attribute either, so nothing could be joined anywhere.
  //
  // Meanwhile both alt arms led with the woo objection while the ad carrying
  // ~all paid traffic (Conv R1 | peeps-table | 02.01) sold the relational
  // problem, so 2/3 of that traffic hit a mismatched hero for 27 days.
  //
  // Flipped to control-only. The alt arms are REMOVED from `variants` (not
  // just zero-weighted) on purpose: resolve() only honors a cookie whose
  // value still exists in this map (mt-ab.js:97), so deleting them re-flips
  // the ~17% of returning visitors holding a stale alt cookie instead of
  // letting them decay over 30 days. Control = offer_v1's own hero copy,
  // which is the closest scent match to the live ad.
  //
  // Do NOT restart this test until variant reaches the conversion side.
  // Arms preserved verbatim below for whoever re-runs it.
  //   alt_woo_plain: { text: {
  //     'hero.h1': "Tarot for when you're not that into the woo",
  //     'hero.h2': 'No belief or buy-in required.' } },
  //   alt_woo_table: { text: {
  //     'hero.h1': "Tarot for when you're not that into the woo",
  //     'hero.h2': 'For everyone at the table.' } }
  hero_copy_v1: {
    description: 'HALTED 2026-07-30. Control-only: offer_v1 hero copy stands for everyone.',
    weights: { control: 100 },
    variants: {
      control: {}
    }
  }

};

// ----- Stg 7 Position HIT: a page for each test slot, each split three ways -----
// Ad links pin the parent (?ab_theme_v1=occult_belief&ab_offer_v1=bundle_resonance
// or bundle_companion). Each parent splits its visitors 34/33/33 among three
// sub-pages (its `split`), chosen once and kept in the cookie; the sub-page's
// name reaches the cart, GA4 and Clarity, so every order and checkout says
// which headline it saw. Every page here is the relational page (the control's)
// with the top changed; the text is copied from bundle_relational at load.
// Words between stars take the hero's accent color (mt-hero-scene.liquid).
// A line left out keeps the control's. The lines are Scott's test picks of
// 2026-09-30 evening ("Lets ship this!"), A, B and C in each slot.
(function (config) {
  var offer = config && config.offer_v1;
  var base = offer && offer.variants && offer.variants.bundle_relational;
  if (!base || !base.text) return;
  var eyebrow = '✦ Funny + surprisingly deep ✦';
  // Bullets, " | " between rows (Scott, 2026-10-01, after a CRO call: bullets read faster). Live had the paragraph:
  // 'Two 78 card decks + thoughtful guidebooks that explain the original cards as if they also went through therapy, dating apps, housing crises, and being raised by boomers.'
  var paragraph = 'Two 78-card decks + thoughtful guidebooks that get us | Rider-Waite cards that also went through therapy, dating apps, housing crises, and being raised by boomers | Understand every card + give readings everyone loves';
  // On every page version, the control's included (Scott, 2026-10-01: "for all of them, across all the variations. even the control").
  base.text['hero.body'] = paragraph;
  var resonance = {
    bundle_resonance_a: { 'hero.eyebrow-bundle': eyebrow, 'hero.h1': 'Relatable Tarot for healing *Millennials*.', 'hero.h2': 'And Gen-Z since they copy us anyway.', 'hero.body': paragraph },
    bundle_resonance_b: { 'hero.eyebrow-bundle': eyebrow, 'hero.h1': "Tarot that's been to *therapy* and gets it.", 'hero.h2': 'All 78 Rider-Waite cards but updated', 'hero.body': paragraph },
    bundle_resonance_c: { 'hero.eyebrow-bundle': eyebrow, 'hero.h1': 'Self-aware tarot for burnt out *Millennials*.', 'hero.h2': "And Gen-Z, because let's be real. They copy us.", 'hero.body': paragraph }
  };
  // Slot 2's three arms, from Scott's screenshots of 2026-09-30 night ("those are the three"): his own eyebrow and the Resonance
  // paragraph on all three. The arm names stay a, b and c; the words changed on the date in the plan's log.
  var companionEyebrow = '✦ Fun + surprisingly deep ✦';
  var companion = {
    bundle_companion_a: { 'hero.eyebrow-bundle': companionEyebrow, 'hero.h1': 'Tarot for *Millennial hosts* and their *people*.', 'hero.h2': 'The most fun decks for social readings', 'hero.body': paragraph },
    bundle_companion_b: { 'hero.eyebrow-bundle': companionEyebrow, 'hero.h1': 'The best tarot decks for *reading with others*.', 'hero.h2': 'Everyone understands the cards\' meanings', 'hero.body': paragraph },
    bundle_companion_c: { 'hero.eyebrow-bundle': companionEyebrow, 'hero.h1': 'Tarot for *Millennial readers* and their people.', 'hero.h2': 'The most fun decks for social readings', 'hero.body': paragraph }
  };
  // Cohort 2 (launch 2026-10-14 if Ready): In the middle of something (slot 1) and Clarity (slot 2).
  // Proposed A, B and C for Scott's review in the playground; staging only until he says push.
  // bundle_clarity used to be its own July page; it is now the relational page with the top changed, like the others.
  var middle = {
    // Scott's picks in the playground, 2026-10-01 (second round)
    bundle_middle_a: { 'hero.eyebrow-bundle': eyebrow, 'hero.h1': 'For spiritual Millennials that are *going through it*.', 'hero.h2': 'Clarity from tarot in words you understand' },
    bundle_middle_b: { 'hero.eyebrow-bundle': eyebrow, 'hero.h1': 'Get *unblocked* with cards that *get it*.', 'hero.h2': 'Rider-Waite tarot that looks like your life' },
    bundle_middle_c: { 'hero.eyebrow-bundle': eyebrow, 'hero.h1': 'Timeless wisdom that *hits different*.', 'hero.h2': 'Rider-Waite tarot but about our struggles' }
  };
  var clarityQuote = { 'hero.featured-quote': '"I get what the cards are trying to say immediately."', 'hero.featured-quote-attr': '— @fairlighttarot, Instagram' };
  var clarity = {
    // Scott's picks in the playground, 2026-10-01
    bundle_clarity_a: { 'hero.eyebrow-bundle': eyebrow, 'hero.h1': 'Instantly *understand* every tarot card.', 'hero.h2': 'Rider-Waite tarot in our words' },
    bundle_clarity_b: { 'hero.eyebrow-bundle': eyebrow, 'hero.h1': 'Understand tarot in *our language*.', 'hero.h2': 'Rider-Waite cards reinterpreted for today.' },
    bundle_clarity_c: { 'hero.eyebrow-bundle': eyebrow, 'hero.h1': 'Accessible tarot for *curious Millennials*.', 'hero.h2': 'Instantly understand every tarot card.' }
  };
  for (var c in clarity) for (var q in clarityQuote) clarity[c][q] = clarityQuote[q];
  // Cohort 3 (Going deep and The gift), Scott's approved picks 2026-10-07 in What to Run Next, Page copy.
  // Each has its own eyebrow and its own three-row paragraph, in place of the control's (the Resonance ads' words).
  var deepEyebrow = '✦ Fun, self-aware tarot ✦';
  var deepBody = "Two 78-card decks + guidebooks that go deeper than the card names | Rider-Waite cards that don't predict. They help you reflect. | Understand every card + read for yourself";
  // Lines under the headline (hero.h2) carry no closing period on these pages; the headline keeps its period (Scott, 2026-10-07).
  var deep = {
    bundle_deep_a: { 'hero.eyebrow-bundle': '✦ Every card, our words ✦', 'hero.h1': 'Thoughtful tarot for *overthinking Millennials*.', 'hero.h2': 'Recognize your patterns instantly', 'hero.body': deepBody },
    bundle_deep_b: { 'hero.eyebrow-bundle': '✦ Every card, in our words ✦', 'hero.h1': 'For *anxious* Millennials *doing the work*.', 'hero.h2': 'An easier way to understand your patterns', 'hero.body': deepBody },
    bundle_deep_c: { 'hero.eyebrow-bundle': deepEyebrow, 'hero.h1': 'Understand your patterns in *words you understand*.', 'hero.h2': 'Simple and deep tarot for overthinking Millennials', 'hero.body': deepBody }
  };
  var giftEyebrow = '✦ Fun, self-aware tarot ✦';
  var giftBody = "Two 78-card decks + guidebooks, each in a keepsake box | No tarot know-how needed to give it | They'll understand every card + read for anyone";
  var gift = {
    bundle_gift_a: { 'hero.eyebrow-bundle': giftEyebrow, 'hero.h1': 'Fun tarot for your *therapy-loving bestie*.', 'hero.h2': 'The gift that gets you two talking for hours', 'hero.body': giftBody },
    bundle_gift_b: { 'hero.eyebrow-bundle': giftEyebrow, 'hero.h1': 'The thoughtful gift that makes them *feel seen*.', 'hero.h2': 'Every card in words they know', 'hero.body': giftBody },
    bundle_gift_c: { 'hero.eyebrow-bundle': giftEyebrow, 'hero.h1': 'The tarot gift that gets you two *talking for hours*.', 'hero.h2': "They'll lose it at the card names", 'hero.body': giftBody }
  };
  var pages = {};
  for (var key in resonance) pages[key] = resonance[key];
  for (var key2 in companion) pages[key2] = companion[key2];
  for (var key3 in middle) pages[key3] = middle[key3];
  for (var key4 in clarity) pages[key4] = clarity[key4];
  for (var key5 in deep) pages[key5] = deep[key5];
  for (var key6 in gift) pages[key6] = gift[key6];
  pages.bundle_resonance = resonance.bundle_resonance_a;   // the parent shows A if the split ever fails
  pages.bundle_companion = companion.bundle_companion_a;
  pages.bundle_middle = middle.bundle_middle_a;
  pages.bundle_clarity = clarity.bundle_clarity_a;
  pages.bundle_deep = deep.bundle_deep_a;
  pages.bundle_gift = gift.bundle_gift_a;
  for (var name in pages) {
    if (!Object.prototype.hasOwnProperty.call(pages, name)) continue;
    var text = {};
    for (var k in base.text) {
      if (Object.prototype.hasOwnProperty.call(base.text, k)) text[k] = base.text[k];
    }
    for (var line in pages[name]) {
      if (Object.prototype.hasOwnProperty.call(pages[name], line)) text[line] = pages[name][line];
    }
    offer.variants[name] = { heroOffer: 'bundle', text: text };
    if (offer.weights && !Object.prototype.hasOwnProperty.call(offer.weights, name)) offer.weights[name] = 0;
  }
  offer.variants.bundle_resonance.split = { bundle_resonance_a: 0, bundle_resonance_b: 0, bundle_resonance_c: 100 };
  offer.variants.bundle_companion.split = { bundle_companion_a: 36, bundle_companion_b: 64, bundle_companion_c: 0 };
  offer.variants.bundle_middle.split = { bundle_middle_a: 34, bundle_middle_b: 33, bundle_middle_c: 33 };
  offer.variants.bundle_clarity.split = { bundle_clarity_a: 0, bundle_clarity_b: 100, bundle_clarity_c: 0 };
  offer.variants.bundle_deep.split = { bundle_deep_a: 34, bundle_deep_b: 33, bundle_deep_c: 33 };
  offer.variants.bundle_gift.split = { bundle_gift_a: 100, bundle_gift_b: 0, bundle_gift_c: 0 };
  // The home page for visitors without an ad (Scott, 2026-10-16): "no belief or buy-in" is retired. The two
  // social lines (Companion A and B, the closest to the Halloween emails) split 50/50 under their own names,
  // so organic and email visitors never mix into the ad slots' headline read. bundle_theme_anchored keeps its
  // words but carries the same split, so a returning visitor's 30-day cookie lands on a new line too.
  offer.variants.home_companion_a = { heroOffer: 'bundle', text: offer.variants.bundle_companion_a.text };
  offer.variants.home_companion_b = { heroOffer: 'bundle', text: offer.variants.bundle_companion_b.text };
  offer.variants.bundle_home = { heroOffer: 'bundle', text: offer.variants.bundle_companion_a.text,
    split: { home_companion_a: 50, home_companion_b: 50 } };
  offer.variants.bundle_theme_anchored.split = offer.variants.bundle_home.split;
  offer.weights.bundle_theme_anchored = 0;
  offer.weights.home_companion_a = 0;
  offer.weights.home_companion_b = 0;
  offer.weights.bundle_home = 100;
  // GA4 reports one experiment as user properties; from 2026-09-30 it is this one, so
  // add-to-carts and purchases in GA4 carry the headline arm. demo_video_v1 gave it up
  // (see its note); the cart attributes and Clarity still carry every experiment.
  offer.primary = true;
})(window.MT_AB_VARIANTS);
